import { defineNuxtPlugin } from "#app";
import { useAnalytics } from "~/composables/useAnalytics";

// RUM (real-user-monitoring) для Core Web Vitals + FCP/TTFB. Bypasses lab
// PSI variance — собирает реальные метрики с реальных устройств/сетей,
// шлёт в GA4 + Yandex Metrika как event "web_vital".
//
// CrUX field data доступна Google'у только когда сайт перешагнёт трафик-
// threshold (≥4k реальных просмотров за 28-day window). До этого momentа
// единственный способ узнать настоящие p75 LCP/INP/CLS — собственный RUM.
//
// GA4: Reports → Engagement → Events → web_vital. Можно фильтровать по
// `metric_name` (LCP/INP/CLS/FCP/TTFB) и сегментировать по device_category,
// page_location, etc. Looker Studio для per-route distributions.
//
// Library size: ~1.5 KB compressed. Lazy-imported после mount чтобы не
// раздувать initial chunks. Stub'ы window.gtag/window.ym устанавливаются
// синхронно в plugins/analytics.client.ts (alphabetically before this file)
// → ранние события (TTFB до загрузки реальных скриптов) буферизуются
// в dataLayer / ym.a и replay'ятся когда tag.js загрузится через rIC.
//
// Skip in dev — analytics scripts не инжектятся локально (см. analytics.client.ts).
//
// Library reference: https://github.com/GoogleChrome/web-vitals
// FID было убрано September 2024 — теперь только INP. Этот плагин на v5
// уже без FID.

interface WebVitalMetric {
  name: "CLS" | "INP" | "LCP" | "FCP" | "TTFB";
  value: number;
  id: string;
  rating: "good" | "needs-improvement" | "poor";
  navigationType: string;
}

export default defineNuxtPlugin(async () => {
  if (!import.meta.env.PROD) return;

  // Skip on operator/closed routes — keeps the web-vitals chunk off
  // /admin and avoids leaking /booking/<token> page_location into events.
  if (isAnalyticsSuppressed(location.pathname)) return;

  // Defer the import so it doesn't count toward initial bundle. The dynamic
  // import resolves after page hydration; web-vitals callbacks fire on
  // appropriate timing events (paint, INP interaction, page hidden).
  const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import("web-vitals");
  const { trackEvent } = useAnalytics();

  const send = (m: WebVitalMetric) => {
    // CLS is a unitless score (typical 0-0.5); round to 3 decimals to keep
    // GA4 numeric precision. Other metrics are ms — round to integer.
    const rounded =
      m.name === "CLS"
        ? Math.round(m.value * 1000) / 1000
        : Math.round(m.value);

    trackEvent("web_vital", {
      metric_name: m.name,
      metric_value: rounded,
      metric_rating: m.rating, // good / needs-improvement / poor
      metric_id: m.id, // unique per page-load, lets you dedupe in BigQuery
      metric_navigation_type: m.navigationType, // navigate / reload / back-forward / prerender
    });
  };

  // Each handler may fire multiple times on the same page (e.g. INP can
  // be re-reported as worse interactions happen). web-vitals dedupes by
  // delta internally — we just forward whatever it tells us, and the
  // metric_id field lets downstream consumers pick the latest sample.
  onCLS(send);
  onINP(send);
  onLCP(send);
  onFCP(send);
  onTTFB(send);
});
